import Parse from 'parse';

import { Injectable } from '@angular/core';
import {
  AnalysisTopology,
  AssetTopology,
  FindingTopology,
  LatestVersionTopology,
  ObjectTopology,
  ParseBaseRepository,
  PipelineStepTopology,
  SampleTopology,
} from '@telespot/infrastructure/parse';
import {
  type FindingRepository,
  ListAssetFindingsPayload,
  ListFindingsForAnalystInSamplePayload,
  ListSampleFindingsPayload,
} from '@analysis/use-cases';
import { Finding, StepTask } from '@telespot/domain';
import { ApiAuthErrors } from '@telespot/web-core';
import { TelespotFindingMapper } from './telespot-finding.mapper';

@Injectable({
  providedIn: 'root',
})
export class TelespotFindingService extends ParseBaseRepository implements FindingRepository {
  private readonly BATCH_SIZE = 50;

  private readonly mapper = new TelespotFindingMapper(this.parse);

  constructor() {
    super(Parse, { useMasterKey: false });
  }

  public async findFindingsForAsset(payload: ListAssetFindingsPayload): Promise<Finding[]> {
    const { assetId, sampleId, creatorEntity, creatorId, pipelineStepIds, limit } = payload;
    const pipelineStepRefs = pipelineStepIds.map((psid) =>
      this.subclasses.getSubclass(PipelineStepTopology.TABLE).createWithoutData(psid)
    );

    const analysisQuery = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.ASSET, this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(assetId))
      .equalTo(AnalysisTopology.SAMPLE, this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId));

    if (creatorEntity === 'user')
      analysisQuery.equalTo(AnalysisTopology.CREATED_BY, this.parse.User.createWithoutData(creatorId));
    else if (creatorEntity === 'algorithm') analysisQuery.doesNotExist(AnalysisTopology.CREATED_BY);

    const analysisFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesQuery(FindingTopology.ANALYSIS, analysisQuery)
      .containedIn(FindingTopology.PIPELINE_STEP, pipelineStepRefs)
      .equalTo(FindingTopology.CREATOR_ID, creatorId)
      .equalTo(FindingTopology.CREATOR_ENTITY, creatorEntity);

    const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, analysisFindingsQuery);

    const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
      .containedIn(FindingTopology.TYPE, [StepTask.TEXT, StepTask.CLASSIFICATION])
      .include([FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP]);

    const pages = this.getTokenPaginationGenerator({ query: findingsQuery, limit: limit ?? this.BATCH_SIZE });

    try {
      const findings = [];

      for await (const items of pages) findings.push(...items.map((f) => this.mapper.toDomain(f)));

      return findings;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async findSampleFindings(payload: ListSampleFindingsPayload): Promise<Finding[]> {
    const { sampleId, creatorEntity, creatorId, pipelineStepIds, limit } = payload;
    const pipelineStepRefs = pipelineStepIds.map((psid) =>
      this.subclasses.getSubclass(PipelineStepTopology.TABLE).createWithoutData(psid)
    );
    const analysisQuery = new this.parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(sampleId))
      .doesNotExist(AnalysisTopology.ASSET);

    if (creatorEntity === 'user')
      analysisQuery.equalTo(AnalysisTopology.CREATED_BY, this.parse.User.createWithoutData(creatorId));
    else if (creatorEntity === 'algorithm') analysisQuery.doesNotExist(AnalysisTopology.CREATED_BY);

    const analysisFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesQuery(FindingTopology.ANALYSIS, analysisQuery)
      .containedIn(FindingTopology.PIPELINE_STEP, pipelineStepRefs)
      .equalTo(FindingTopology.CREATOR_ID, creatorId)
      .equalTo(FindingTopology.CREATOR_ENTITY, creatorEntity);

    const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, analysisFindingsQuery);

    const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
      .containedIn(FindingTopology.TYPE, [StepTask.TEXT, StepTask.CLASSIFICATION])
      .include([FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP]);

    const pages = this.getTokenPaginationGenerator({ query: findingsQuery, limit: limit ?? this.BATCH_SIZE });

    try {
      const findings = [];

      for await (const items of pages) findings.push(...items.map((f) => this.mapper.toDomain(f)));

      return findings;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async listForAnalystInSample(payload: ListFindingsForAnalystInSamplePayload): Promise<Finding[]> {
    const sampleRef = this.subclasses.getSubclass(SampleTopology.TABLE).createWithoutData(payload.sampleId);

    const analysisQuery = new this.parse.Query(AnalysisTopology.TABLE).equalTo(AnalysisTopology.SAMPLE, sampleRef);

    const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesQuery(FindingTopology.ANALYSIS, analysisQuery)
      .equalTo(FindingTopology.CREATOR_ID, payload.analystId);

    if (payload?.findingTypes?.length > 0) {
      findingsQuery.containedIn(FindingTopology.TYPE, payload.findingTypes);
    }

    const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .matchesKeyInQuery(LatestVersionTopology.VERSION_ID, ObjectTopology.ID, findingsQuery);

    const latestFindingsQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
      .include([FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP]);

    const pages = this.getTokenPaginationGenerator({ query: latestFindingsQuery, limit: this.BATCH_SIZE });

    try {
      const findings: Finding[] = [];

      for await (const items of pages) findings.push(...items.map((f) => this.mapper.toDomain(f)));

      return findings;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  public async fetchLatestByIds(ids: string[]): Promise<Finding[]> {
    const findings: Finding[] = [];
    const fieldsToInclude = [FindingTopology.ANALYSIS, FindingTopology.PIPELINE_STEP];

    try {
      while (ids.length > 0) {
        const batch = ids.splice(0, Math.min(this.BATCH_SIZE, ids.length));

        const latestVersionQuery = new this.parse.Query(LatestVersionTopology.TABLE)
          .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
          .containedIn(LatestVersionTopology.ENTITY_ID, batch);

        const findingsQuery = new this.parse.Query(FindingTopology.TABLE)
          .matchesKeyInQuery(ObjectTopology.ID, LatestVersionTopology.VERSION_ID, latestVersionQuery)
          .include(fieldsToInclude);

        const results = await findingsQuery.find();

        findings.push(...results.map((r) => this.mapper.toDomain(r)));
      }

      return findings;
    } catch (error) {
      throw this.handleAuthError(error, ApiAuthErrors.UNAUTHORIZED);
    }
  }

  // REVIEW: tiene mucha logica espeficica de front. Esto debe quedarse en las ultimas 3 lineas.
  public async saveAll(findings: Finding[], sampleId: string): Promise<Finding[]> {
    const parseObjects = this.mapper.fromDomain(findings, sampleId);
    const results = await this.parse.Object.saveAll(parseObjects, this.options);

    return results.map((r) => this.mapper.toDomain(r));
  }
}
